import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { BUILD_NETWORK, Network } from "../utils/network";
// Type-only: AuthContext imports setAuthUid from this slice, so a value import of
// "auth" here would close a runtime require cycle
// (auth/index -> AuthContext -> store/userSlice -> auth/index) and could leave one
// of the bindings undefined at module-eval time depending on entry order.
import type { MerchantProfile } from "auth";

interface userState {
  network: Network | undefined;
  // The Firebase uid this tab's stored identity (merchantId / merchantMetadata /
  // userType) actually belongs to. Written ONLY by a sign-in performed in this
  // tab, never by a cross-tab sync — that asymmetry is the whole point. Firebase's
  // user is origin-shared, so when another tab signs someone else in, this tab's
  // Firebase user changes while authUid does not, and the mismatch is detectable.
  // Without it, `userType: "merchant"` says nothing about WHICH merchant.
  authUid: string | undefined;
  merchantId: string | undefined;
  merchantInfo: any;
  merchantMetadata: MerchantProfile | undefined;
  merchantBalance: any;
  merchantTransactions: any[] | undefined;
  userType: string | undefined;
  showLoadMore: boolean;
  cardNumberMap: Record<string, string>;
  /** Raw preference; the effective token is derived in features/tokens/useSettlementToken. */
  settlementTokenAddress: string | undefined;
}

const initialState: userState = {
  network: BUILD_NETWORK,
  authUid: undefined,
  merchantId: undefined,
  merchantInfo: undefined,
  merchantMetadata: undefined,
  merchantBalance: undefined,
  merchantTransactions: undefined,
  userType: undefined,
  showLoadMore: false,
  cardNumberMap: {},
  settlementTokenAddress: undefined,
};

const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAuthUid: (state, action: PayloadAction<string | undefined>) => {
      state.authUid = action.payload;
    },
    setMerchantId: (state, action: PayloadAction<string>) => {
      state.merchantId = action.payload;
    },
    setMerchantInfo: (state, action: PayloadAction<any>) => {
      state.merchantInfo = action.payload;
    },
    setMerchantMetadata: (state, action: PayloadAction<any>) => {
      state.merchantMetadata = action.payload;
    },
    updateMerchantInfo: (
      state,
      action: PayloadAction<Partial<typeof state.merchantInfo>>,
    ) => {
      state.merchantInfo = {
        ...state.merchantInfo,
        ...action.payload,
      };
    },
    setMerchantBalance: (state, action: PayloadAction<any[]>) => {
      state.merchantBalance = action.payload;
    },
    setMerchantTransactions: (state, action: PayloadAction<any[]>) => {
      state.merchantTransactions = action.payload;
    },
    // Payload allows undefined so callers can clear the value back to
    // initialState — the login error path and the post-password-change sign-out
    // both need to drop it. The state field was already `string | undefined`;
    // only the action was narrower. Matches usersApp.
    setUserType: (state, action: PayloadAction<string | undefined>) => {
      state.userType = action.payload;
    },
    setShowLoadMore: (state, action: PayloadAction<boolean>) => {
      state.showLoadMore = action.payload;
    },
    /**
     * Forget the signed-in session. Everything here is redux-persist whitelisted, so without it a
     * sign-out leaves the merchant id, profile and userType in localStorage for the next person to
     * sign in on this browser. `cardNumberMap` goes too — it holds customers' card numbers.
     * `authUid` deliberately survives: the cross-tab identity guards read it, and a null Firebase
     * user already reports "signed-out" on its own.
     */
    resetUserSession: (state) => {
      state.merchantId = undefined;
      state.merchantInfo = undefined;
      state.merchantMetadata = undefined;
      state.merchantBalance = undefined;
      state.merchantTransactions = undefined;
      state.userType = undefined;
      state.showLoadMore = false;
      state.cardNumberMap = {};
    },
    cacheCardNumber: (
      state,
      action: PayloadAction<{ address: string; cardNumber: string }>,
    ) => {
      state.cardNumberMap[action.payload.address] = action.payload.cardNumber;
    },
    setSettlementTokenAddress: (
      state,
      action: PayloadAction<string | undefined>,
    ) => {
      state.settlementTokenAddress = action.payload;
    },
  },
});

export const {
  setAuthUid,
  setMerchantId,
  setMerchantInfo,
  setMerchantMetadata,
  setMerchantBalance,
  setMerchantTransactions,
  setUserType,
  setShowLoadMore,
  resetUserSession,
  cacheCardNumber,
  setSettlementTokenAddress,
} = userSlice.actions;

export default userSlice.reducer;
